import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFSpouseContributionSplitting = ({ smsfState, setSmsfState }) => {
    const [splitData, setSplitData] = useState({
        contributorBalance: 500000,
        contributionAmount: 20000,
        splitPercentage: 85,
        receivingSpouseAge: 45
    });

    const [results, setResults] = useState(null);

    const calculateSplit = () => {
        const maxSplitPct = 85; // ATO limit
        const actualSplitPct = Math.min(splitData.splitPercentage || 0, maxSplitPct);
        
        const contributionAmt = Number(splitData.contributionAmount) || 0;
        const amountToSplit = contributionAmt * (actualSplitPct / 100);
        const contributorRetains = contributionAmt * ((100 - actualSplitPct) / 100);
        
        // Tax implications
        const concessionalCap = Number(FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL) || 32500;
        const splitUsesCapSpace = amountToSplit;
        const remainingCapForSpouse = concessionalCap - splitUsesCapSpace;
        
        setResults({
            amountToSplit: Math.round(amountToSplit),
            contributorRetains: Math.round(contributorRetains),
            receivingSpouseGets: Math.round(amountToSplit),
            actualSplitPct,
            capImpact: Math.round(splitUsesCapSpace),
            remainingCap: Math.round(Math.max(0, remainingCapForSpouse)),
            guidance: actualSplitPct === maxSplitPct 
                ? "Maximum 85% split applied (ATO limit)" 
                : `Splitting ${actualSplitPct}% - you could split up to 85%`
        });
    };
    
    const TBC = FinancialConfig.TRANSFER_BALANCE_CAP;

    return (
        <div className="max-w-5xl mx-auto p-6">
            <h2 className="text-3xl font-bold mb-2">Spouse Contribution Splitting</h2>
            <p className="text-gray-600 mb-6">Split your concessional contributions with your spouse (maximum 85%)</p>

            <div className="space-y-6">
                {/* Inputs */}
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold mb-4">Contribution Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Contributor Super Balance ($)
                            </label>
                            <input 
                                {...getNumericInputProps()} 
                                value={splitData.contributorBalance} 
                                onChange={(e) => setSplitData({ ...splitData, contributorBalance: Number(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                                placeholder="500000"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Contribution Amount ($)
                            </label>
                            <input 
                                {...getNumericInputProps()} 
                                value={splitData.contributionAmount} 
                                onChange={(e) => setSplitData({ ...splitData, contributionAmount: Number(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                                placeholder="20000"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Split Percentage (%) - Max 85%
                            </label>
                            <input 
                                {...getNumericInputProps()} 
                                min="0"
                                max="100"
                                value={splitData.splitPercentage} 
                                onChange={(e) => setSplitData({ ...splitData, splitPercentage: Number(e.target.value) || 0 })}
                                onBlur={(e) => {
                                    const val = Number(e.target.value);
                                    if (val > 85) {
                                        alert('⚠️ Split Percentage Error\n\nMaximum split percentage is 85% under ATO rules.\n\nYour input has been adjusted to 85%.');
                                        setSplitData({ ...splitData, splitPercentage: 85 });
                                    }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                                placeholder="85"
                            />
                            {splitData.splitPercentage > 85 && (
                                <p className="text-red-600 text-sm mt-1">⚠️ Exceeds 85% maximum</p>
                            )}
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Receiving Spouse Age
                            </label>
                            <input 
                                {...getNumericInputProps()} 
                                value={splitData.receivingSpouseAge} 
                                onChange={(e) => setSplitData({ ...splitData, receivingSpouseAge: Number(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
                                placeholder="45"
                            />
                        </div>
                    </div>
                    
                    <button
                        type="button"
                        onClick={calculateSplit}
                        className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-colors"
                    >
                        Calculate Split
                    </button>
                </div>

                {/* Results */}
                {results && (
                    <>
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="font-semibold mb-4">Split Results</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600 mb-1">Amount to Split</div>
                                    <div className="text-2xl font-bold text-blue-600">
                                        ${results.amountToSplit.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        {results.actualSplitPct}% of contribution
                                    </div>
                                </div>
                                
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600 mb-1">Contributor Retains</div>
                                    <div className="text-2xl font-bold text-green-600">
                                        ${results.contributorRetains.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        {100 - results.actualSplitPct}% of contribution
                                    </div>
                                </div>
                                
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <div className="text-sm text-gray-600 mb-1">Receiving Spouse Gets</div>
                                    <div className="text-2xl font-bold text-purple-600">
                                        ${results.receivingSpouseGets.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        Added to spouse's super
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tax Implications */}
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <h4 className="font-semibold text-yellow-900 mb-2">📊 Tax Implications</h4>
                            <div className="text-sm text-yellow-800 space-y-1">
                                <p>• Split amount uses ${results.capImpact.toLocaleString()} of receiving spouse's concessional cap</p>
                                <p>• Remaining concessional cap for spouse: ${results.remainingCap.toLocaleString()}</p>
                                <p>• Both spouses must lodge contribution splitting application (NAT 16880)</p>
                                <p>• Can only split up to 85% of splittable contributions</p>
                            </div>
                        </div>

                        {/* Strategic Guidance */}
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <h4 className="font-semibold text-blue-900 mb-2">💡 Strategic Guidance</h4>
                            <div className="text-sm text-blue-800 space-y-1">
                                <p>• {results.guidance}</p>
                                <p>• Consider: Equalizing balances helps maximize both Transfer Balance Caps (${TBC.toLocaleString()} each)</p>
                                <p>• Benefit: More flexibility in pension phase with two separate accounts</p>
                                <p>• Reminder: Lodge splitting application before tax return is lodged</p>
                            </div>
                        </div>
                    </>
                )}

                {!results && (
                    <div className="bg-gray-50 p-8 rounded-lg text-center text-gray-500">
                        <p>Click "Calculate Split" to see results</p>
                    </div>
                )}
            </div>
        </div>
    );
};

SMSFSpouseContributionSplitting.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFSpouseContributionSplitting;

